import { GUIDE_HOST_ATTR } from '../constants';
import { applyDesignVars } from '../guide-design';
import { GUIDE_STYLES } from '../styles';
import type { GuideRenderContext, GuideStep } from '../types';

/**
 * Base de los renderers de guías. Encapsula:
 *   - creación del host con Shadow DOM (aislamiento de estilos)
 *   - inyección del CSS común
 *   - limpieza del DOM y de listeners en `destroy()`
 *
 * Cada subclase implementa `render(context)`. La subclase puede registrar
 * cleanup adicional con `registerCleanup` para que se ejecute en destroy.
 *
 * Tipos genéricos:
 *   - `TContext` (default `GuideRenderContext`): el shape que recibe la
 *     subclase. WalkthroughRenderer usa uno propio con step + callbacks.
 *   - `TResult` (default `void`): lo que `render` devuelve. El walkthrough
 *     usa `boolean` para señalar éxito al controller cuando el ancla no
 *     aparece. El resto sigue devolviendo `void`/`Promise<void>`.
 */
export abstract class BaseRenderer<TContext = GuideRenderContext, TResult = void> {
  protected host: HTMLElement | null = null;
  protected shadow: ShadowRoot | null = null;
  private cleanups: Array<() => void> = [];

  // ── Update in-place (preview en vivo del editor) ──
  // La subclase setea estas piezas en render() para habilitarlo: el contenedor
  // del contenido, el nodo de contenido actual y cómo reconstruirlo.
  protected liveContainer: HTMLElement | null = null;
  protected liveContent: HTMLElement | null = null;
  protected liveBuild: ((step: GuideStep) => HTMLElement) | null = null;
  /** Clave de anclaje/estructura fijada en render(); si cambia → remontar. */
  protected liveKey = '';

  /** Clave de anclaje derivada del step (override por subclase). */
  protected liveKeyOf(_step: GuideStep): string {
    return '';
  }

  /** Hook post-swap (reposicionar tooltip, actualizar clase del banner…). */
  protected onLiveUpdate(_step: GuideStep): void {}

  /**
   * Actualiza el contenido del step SIN remontar el host — así el preview del
   * editor no parpadea en cada tecla. Devuelve `false` cuando el renderer no
   * lo soporta o el cambio requiere remontar (cambió el ancla/estructura);
   * en ese caso el caller hace el re-render completo.
   */
  updateStep(step: GuideStep): boolean {
    if (!this.host || !this.liveContainer || !this.liveContent || !this.liveBuild) return false;
    if (this.liveKeyOf(step) !== this.liveKey) return false;
    const next = this.liveBuild(step);
    this.liveContainer.replaceChild(next, this.liveContent);
    this.liveContent = next;
    this.applyDesign(step.style);
    this.onLiveUpdate(step);
    return true;
  }

  /**
   * Crea un `<div data-veo-guide>` adjunto a `document.body`, le adjunta
   * un ShadowRoot abierto e inyecta los estilos. Retorna el nodo raíz
   * donde la subclase puede insertar el contenido específico.
   */
  protected createHost(): { host: HTMLElement; shadow: ShadowRoot; root: HTMLElement } {
    const host = document.createElement('div');
    host.setAttribute(GUIDE_HOST_ATTR, '');
    const shadow = host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = GUIDE_STYLES;
    shadow.appendChild(style);

    const root = document.createElement('div');
    shadow.appendChild(root);

    document.body.appendChild(host);

    this.host = host;
    this.shadow = shadow;
    return { host, shadow, root };
  }

  /**
   * Aplica la tematización por datos del step (`step.style`) como variables
   * CSS sobre el host. Llamar tras `createHost()`. No-op si no hay host o
   * style. Ver `guide-design.ts`.
   */
  protected applyDesign(style: GuideStep['style']): void {
    if (this.host) applyDesignVars(this.host, style);
  }

  /**
   * Registra una función a ejecutar en `destroy()`. Útil para limpiar
   * listeners globales (scroll, resize) o intervalos.
   */
  protected registerCleanup(fn: () => void): void {
    this.cleanups.push(fn);
  }

  /**
   * Host de la guía montada (o `null` si aún no se montó / ya se destruyó).
   * Lo usa el modo builder para adjuntar manipulación directa (drag/resize)
   * sobre el shadow root abierto sin tocar los renderers.
   */
  hostElement(): HTMLElement | null {
    return this.host;
  }

  /** Remueve el host del DOM y corre todas las funciones de cleanup. */
  destroy(): void {
    for (const fn of this.cleanups) {
      try {
        fn();
      } catch {
        // No propagar: un cleanup roto no debe romper el resto.
      }
    }
    this.cleanups = [];

    if (this.host?.parentNode) {
      this.host.parentNode.removeChild(this.host);
    }
    this.host = null;
    this.shadow = null;
  }

  abstract render(context: TContext): TResult | Promise<TResult>;
}
